import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { signupUser } from "../api";
import InputField from "../forms/InputField";
import PasswordField from "../forms/PasswordField";
import SelectField from "../forms/SelectField";
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

export default function SignupPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    password: "",
    confirmPassword: "",
    organizationName: "",
    phoneNumber: "",
    companySize: "",
    industry: "",
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const navigate = useNavigate();

  const companySizes = [
    { value: "", label: "Select company size" },
    { value: "1-10", label: "1-10 employees" },
    { value: "11-50", label: "11-50 employees" },
    { value: "51-200", label: "51-200 employees" },
    { value: "201-500", label: "201-500 employees" },
    { value: "500+", label: "500+ employees" },
  ];

  const industries = [
    { value: "", label: "Select industry" },
    { value: "healthcare", label: "Healthcare & Medical" },
    { value: "retail", label: "Retail & E-commerce" },
    { value: "hospitality", label: "Hospitality & Food Service" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "technology", label: "Technology & IT" },
    { value: "education", label: "Education" },
    { value: "finance", label: "Finance & Banking" },
    { value: "logistics", label: "Logistics & Transportation" },
    { value: "other", label: "Other" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
    setApiError(null);
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
      if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
      if (!formData.emailId) {
        newErrors.emailId = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.emailId)) {
        newErrors.emailId = "Email is invalid";
      }
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = "Phone number is required";
      } else if (!/^\+?[\d\s-()]+$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = "Phone number is invalid";
      }
    } else if (step === 2) {
      if (!formData.organizationName.trim()) {
        newErrors.organizationName = "Organization name is required";
      }
      if (!formData.companySize) {
        newErrors.companySize = "Please select company size";
      }
      if (!formData.industry) {
        newErrors.industry = "Please select industry";
      }
    } else if (step === 3) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = "Password must include uppercase, lowercase, and number";
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = "You must agree to the terms and conditions";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setApiError(null);
    setLoading(true);

    try {
      const { confirmPassword, agreeToTerms, ...dataToSend } = formData;
      const res = await signupUser(dataToSend);

      if (res.data?.success || res.status === 200 || res.status === 201) {
        setSuccessMessage(res.data?.message || "Signup successful! Redirecting...");
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setApiError(res.data?.message || "Signup failed. Please try again.");
      }
    } catch (err) {
      setApiError(
        err.response?.data?.message || 
        "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const ParticleBackground = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-white/10 rounded-full"
          initial={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth / 2 : 500),
            y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
          }}
          animate={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth / 2 : 500),
            y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
          }}
          transition={{
            duration: Math.random() * 15 + 10,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      ))}
    </div>
  );

  const steps = [
    { number: 1, title: "Personal Info", description: "Tell us about yourself" },
    { number: 2, title: "Company Details", description: "About your organization" },
    { number: 3, title: "Security", description: "Set up your password" },
  ];

  return (
    <>
      <LandingNav />

      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-80px)] relative overflow-hidden">
        
        {/* Left Side - Illustration */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full lg:w-1/2 bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col justify-center items-center p-8 md:p-12 relative"
        >
          {/* Decorative elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative z-10 bg-white rounded-2xl p-6 shadow-2xl"
          >
            <motion.img 
              src="/signup.png" 
              alt="Signup" 
              className="w-full max-w-sm"
              animate={{ 
                y: [0, -10, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 text-center space-y-4 relative z-10"
          >
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Join Shiftry Today
            </h1>
            <p className="text-gray-600 text-sm md:text-base max-w-md">
              Start managing your workforce efficiently with smart scheduling, attendance tracking, and seamless shift management.
            </p>
            
            {/* Feature highlights */}
            <div className="grid grid-cols-1 gap-4 mt-6 text-left max-w-md">
              {[
                { icon: "📅", text: "Smart shift scheduling" },
                { icon: "👥", text: "Team management tools" },
                { icon: "📊", text: "Real-time analytics" },
                { icon: "🔔", text: "Instant notifications" },
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6 + idx * 0.1 }}
                  className="flex items-center space-x-3 bg-white/50 backdrop-blur-sm rounded-lg p-3"
                >
                  <span className="text-2xl">{feature.icon}</span>
                  <span className="text-gray-700 text-sm font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 flex justify-center items-center p-6 md:p-12 relative">
          <ParticleBackground />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="w-full max-w-lg relative z-10"
          >
            <motion.div
              className="bg-white/95 backdrop-blur-lg p-8 md:p-10 rounded-2xl shadow-2xl border border-white/20"
              whileHover={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
            >
              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  {steps.map((step, idx) => (
                    <React.Fragment key={step.number}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4 + idx * 0.1 }}
                        className="flex flex-col items-center flex-1"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                          currentStep >= step.number
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          {currentStep > step.number ? '✓' : step.number}
                        </div>
                        <span className={`text-xs mt-2 font-medium hidden sm:block ${
                          currentStep >= step.number ? 'text-blue-600' : 'text-gray-400'
                        }`}>
                          {step.title}
                        </span>
                      </motion.div>
                      {idx < steps.length - 1 && (
                        <div className="flex-1 h-1 mx-2 rounded-full bg-gray-200 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ 
                              width: currentStep > step.number ? '100%' : '0%' 
                            }}
                            transition={{ duration: 0.5 }}
                            className="h-full bg-gradient-to-r from-blue-600 to-blue-700"
                          />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Form Header */}
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                  {steps[currentStep - 1].title}
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  {steps[currentStep - 1].description}
                </p>
              </motion.div>

              {/* Error/Success Messages */}
              <AnimatePresence>
                {apiError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <p className="text-red-600 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {apiError}
                    </p>
                  </motion.div>
                )}

                {successMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <p className="text-green-600 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {successMessage}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Form Content */}
              <form onSubmit={handleSubmit}>
                <AnimatePresence mode="wait">
                  {/* Step 1: Personal Information */}
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`transition-all duration-300 ${focusedField === 'firstName' ? 'transform scale-[1.02]' : ''}`}>
                          <InputField
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('firstName')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="John"
                            error={errors.firstName}
                            required
                          />
                        </div>
                        <div className={`transition-all duration-300 ${focusedField === 'lastName' ? 'transform scale-[1.02]' : ''}`}>
                          <InputField
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('lastName')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="Doe"
                            error={errors.lastName}
                            required
                          />
                        </div>
                      </div>

                      <div className={`transition-all duration-300 ${focusedField === 'emailId' ? 'transform scale-[1.02]' : ''}`}>
                        <InputField
                          label="Email Address"
                          name="emailId"
                          type="email"
                          value={formData.emailId}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('emailId')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="john.doe@company.com"
                          error={errors.emailId}
                          required
                        />
                      </div>

                      <div className={`transition-all duration-300 ${focusedField === 'phoneNumber' ? 'transform scale-[1.02]' : ''}`}>
                        <InputField
                          label="Phone Number"
                          name="phoneNumber"
                          type="tel"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('phoneNumber')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="+1 (555) 123-4567"
                          error={errors.phoneNumber}
                          required
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Company Details */}
                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className={`transition-all duration-300 ${focusedField === 'organizationName' ? 'transform scale-[1.02]' : ''}`}>
                        <InputField
                          label="Organization Name"
                          name="organizationName"
                          value={formData.organizationName}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('organizationName')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="Your Company Inc."
                          error={errors.organizationName}
                          required
                        />
                      </div>

                      <div className={`transition-all duration-300 ${focusedField === 'companySize' ? 'transform scale-[1.02]' : ''}`}>
                        <SelectField
                          label="Company Size"
                          name="companySize"
                          value={formData.companySize}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('companySize')}
                          onBlur={() => setFocusedField(null)}
                          options={companySizes}
                          error={errors.companySize}
                          required
                        />
                      </div>

                      <div className={`transition-all duration-300 ${focusedField === 'industry' ? 'transform scale-[1.02]' : ''}`}>
                        <SelectField
                          label="Industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('industry')}
                          onBlur={() => setFocusedField(null)}
                          options={industries}
                          error={errors.industry}
                          required
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Password & Terms */}
                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className={`transition-all duration-300 ${focusedField === 'password' ? 'transform scale-[1.02]' : ''}`}>
                        <PasswordField
                          label="Password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('password')}
                          onBlur={() => setFocusedField(null)}
                          show={showPassword}
                          toggle={togglePasswordVisibility}
                          placeholder="••••••••"
                          error={errors.password}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Must be 8+ characters with uppercase, lowercase, and number
                        </p>
                      </div>

                      <div className={`transition-all duration-300 ${focusedField === 'confirmPassword' ? 'transform scale-[1.02]' : ''}`}>
                        <PasswordField
                          label="Confirm Password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('confirmPassword')}
                          onBlur={() => setFocusedField(null)}
                          show={showPassword}
                          toggle={togglePasswordVisibility}
                          placeholder="••••••••"
                          error={errors.confirmPassword}
                          required
                        />
                      </div>

                      <div className="pt-2">
                        <label className="flex items-start space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            name="agreeToTerms"
                            checked={formData.agreeToTerms}
                            onChange={handleChange}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-600 group-hover:text-gray-800">
                            I agree to the{" "}
                            <a href="/terms" className="text-blue-600 hover:text-blue-800 font-medium underline">
                              Terms and Conditions
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" className="text-blue-600 hover:text-blue-800 font-medium underline">
                              Privacy Policy
                            </a>
                          </span>
                        </label>
                        {errors.agreeToTerms && (
                          <p className="text-red-600 text-xs mt-1">{errors.agreeToTerms}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 gap-4">
                  {currentStep > 1 && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={prevStep}
                      className="flex items-center px-6 py-3 text-blue-600 border-2 border-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </motion.button>
                  )}

                  {currentStep < 3 ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={nextStep}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all relative overflow-hidden group"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Continue
                        <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading || !!successMessage}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating Account...
                          </>
                        ) : successMessage ? (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Success!
                          </>
                        ) : (
                          <>
                            Create Account
                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </>
                        )}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                    </motion.button>
                  )}
                </div>
              </form>

              {/* Login Link */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="mt-6 text-center"
              >
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-blue-600 font-semibold hover:text-blue-800 transition-colors duration-200 hover:underline"
                  >
                    Sign in here
                  </button>
                </p>
              </motion.div>

              {/* Security Badge */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-6 flex items-center justify-center text-xs text-gray-500"
              >
                <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                256-bit SSL encrypted signup
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <FooterLanding />
    </>
  );
}
